import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Plus, Minus, ArrowRight, ArrowDown, Play, X } from "lucide-react";
import { MinimalShell } from "../components/layout/MinimalShell";
import { LeadModal } from "../components/shared/LeadCapture";
import { PhoneVideo } from "../components/shared/PhoneVideo";
import { POA } from "../data/portalsContent";

// Priority 2 — Portals of Awe / Founding Circle (Task List §26–§44).
// Section order is mandated by the brief's closing direction:
//   Portals of Awe → community & experience → Vital Neuro technology → pathways and
//   practices → live Resonance Circle → what becomes possible → human proof →
//   Vital Neuro proof → invitation to join.
// The visitor is joining a community; the technology makes the membership more valuable.

const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth" }); };

const Eyebrow = ({ children }) => (
  <p className="text-sm tracking-[0.24em] uppercase text-[#E7CC86]/80 mb-4">{children}</p>
);

const Quote = ({ quote, name, title, testid }) => (
  <figure data-testid={testid} className="max-w-2xl mx-auto text-center">
    <blockquote className="font-serif italic text-xl md:text-2xl text-cream/85 leading-relaxed">
      “{quote}”
    </blockquote>
    <figcaption className="mt-5">
      <span className="block font-semibold text-[#E7CC86]">{name}</span>
      <span className="block text-sm text-cream/50 mt-0.5">{title}</span>
    </figcaption>
  </figure>
);

export default function PortalsOfAwePage() {
  const [joinOpen, setJoinOpen] = useState(false);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [tab, setTab] = useState(POA.first7.tabs[0].key);
  const [openFaq, setOpenFaq] = useState(0);
  const activeTab = POA.first7.tabs.find((t) => t.key === tab) || POA.first7.tabs[0];

  const openJoin = () => setJoinOpen(true);

  return (
    <MinimalShell>
      {/* ── SCREEN 1 · FOUNDING CIRCLE ENTRY (§28) ───────────────────────
          Portals of Awe and the Founding Circle are established first; Vital Neuro
          is introduced afterwards as an included dimension of the membership. */}
      <section data-testid="poa-page" className="relative pt-32 md:pt-40 pb-20 overflow-hidden grain">
        <div className="aurora-weave" />
        <div className="line-weave" />
        <div className="absolute inset-0 hex-texture opacity-40 pointer-events-none" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 text-center">
          <motion.img initial={{ opacity: 0, y: 12 }} animate={{ opacity: 0.95, y: 0 }} transition={{ duration: 0.8 }}
            src="/assets/logos/gary-signature-poa-white.webp" alt="Portals of Awe"
            className="h-20 md:h-28 w-auto mx-auto mb-8" onError={(e) => { e.currentTarget.style.display = "none"; }} />

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="text-sm tracking-[0.3em] uppercase text-[#E7CC86]/80 mb-4">{POA.hero.eyebrow}</motion.p>

          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.06] text-cream">
            {POA.hero.h1}
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="font-serif italic text-xl md:text-2xl text-gold mt-4">{POA.hero.sub}</motion.p>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.38 }}
            className="text-base md:text-lg text-cream/70 leading-relaxed max-w-2xl mx-auto mt-7">
            {POA.hero.supporting}
          </motion.p>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.44 }}
            className="text-sm tracking-[0.12em] text-cream/60 mt-8">{POA.hero.membership}</motion.p>

          {/* Vital Neuro enters here — an included dimension, not the identity of the offer */}
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.52 }}
            className="mt-10 max-w-xl mx-auto rounded-2xl border border-[#C9A24B]/25 bg-[#080B12]/50 px-6 py-6">
            <p className="text-xs tracking-[0.24em] uppercase text-[#E7CC86]/75">{POA.hero.includingLabel}</p>
            <p className="text-cream/85 mt-2.5">{POA.hero.includingLine}</p>
            <p className="text-sm text-cream/55 mt-2">{POA.hero.includingNote}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-9">
            <button data-testid="poa-hero-explore" onClick={() => scrollTo("poa-experience")}
              className="btn-gold px-8 py-4 rounded-full text-base font-semibold inline-flex items-center gap-2">
              {POA.hero.primaryCta} <ArrowDown size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── SCREEN 2 · WHAT THE VITAL EXPERIENCE IS (§29) ──────────────── */}
      <section id="poa-experience" className="relative py-20 md:py-28 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 text-center">
          <Eyebrow>{POA.whatItIs.eyebrow}</Eyebrow>
          <h2 className="font-display font-semibold text-4xl sm:text-5xl leading-[1.08] text-cream">
            {POA.whatItIs.h}
          </h2>
          <p className="font-display text-3xl sm:text-4xl text-gold leading-[1.12] mt-2">{POA.whatItIs.hSub}</p>
          {POA.whatItIs.body.map((p, i) => (
            <p key={i} className="text-lg text-cream/70 leading-relaxed mt-8">{p}</p>
          ))}
        </div>
      </section>

      {/* ── SCREEN 3 · PORTALS OF AWE + VITAL NEURO (§30) ──────────────── */}
      <section className="relative py-20 md:py-28 bg-[#080B12] overflow-hidden grain">
        <div className="aurora-weave slow" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8">
          <Eyebrow>{POA.field.eyebrow}</Eyebrow>
          <blockquote className="border-l-2 border-[#C9A24B] pl-6 my-6">
            <p className="font-serif italic text-2xl md:text-3xl text-[#E7CC86] leading-snug">{POA.field.quote}</p>
          </blockquote>
          <p className="text-lg text-cream/70 leading-relaxed mt-8">{POA.field.body}</p>
        </div>
      </section>

      {/* ── SCREEN 4 · INSIDE THE EXPERIENCE (§31) ─────────────────────── */}
      <section data-testid="poa-app-section" className="relative py-20 md:py-28 bg-[#0B1020] overflow-hidden">
        <div className="absolute -top-20 left-0 w-[45rem] h-[45rem] radial-glow blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="flex justify-center order-2 lg:order-1">
            <PhoneVideo src="/assets/video/poa-app-90s.mp4" poster="/assets/video/poa-app-poster.jpg" label="90-second preview" testid="poa-app-phone" />
          </motion.div>
          <div className="order-1 lg:order-2">
            <Eyebrow>{POA.inside.eyebrow}</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl leading-[1.08] text-cream mb-5">{POA.inside.h}</h2>
            <p className="text-lg text-cream/70 leading-relaxed mb-6 max-w-xl">{POA.inside.body}</p>
            <p className="font-serif italic text-[#E7CC86] mb-8">{POA.inside.playLine}</p>
            <button data-testid="poa-watch-walkthrough" onClick={() => setWalkthroughOpen(true)}
              className="btn-ghost-gold px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
              <Play size={17} className="text-[#E7CC86]" fill="#E7CC86" /> Watch the preview
            </button>
          </div>
        </div>
      </section>

      {/* ── SCREEN 5 · THE PRACTICE (§32) ──────────────────────────────── */}
      <section className="relative py-20 md:py-28 bg-[#080B12] overflow-hidden grain">
        <div className="aurora-weave slow" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <Eyebrow>{POA.coherence.eyebrow}</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream mb-4">{POA.coherence.h}</h2>
            <p className="text-lg text-cream/65">{POA.coherence.sub}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {POA.coherence.steps.map((s, i) => (
              <motion.div key={s.label} data-testid={`poa-step-${i}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="glass rounded-2xl p-6 hover:border-[#C9A24B]/45 transition-colors">
                <span className="font-display text-2xl text-[#E7CC86]/60">{s.n}</span>
                <h3 className="font-display text-2xl text-cream mt-2">{s.label}</h3>
                <p className="text-cream/55 text-sm mt-2">{s.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE EXPERIENCE ARCHITECTURE (§33/§34) ──────────────────────── */}
      <section className="relative py-20 md:py-28 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <Eyebrow>{POA.layers.eyebrow}</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream mb-4">{POA.layers.h}</h2>
            <p className="text-lg text-cream/65">{POA.layers.sub}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[POA.layers.stateTools, POA.layers.pathways].map((layer) => (
              <div key={layer.title} className="glass rounded-3xl p-8">
                <h3 className="font-display text-2xl text-cream">{layer.title}</h3>
                <p className="font-serif italic text-[#E7CC86] mt-1 mb-6">{layer.q}</p>
                <ul className="space-y-3">
                  {layer.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-cream/75">
                      <Check size={17} className="text-[#E7CC86] shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THREE PATHWAYS (§35) ───────────────────────────────────────── */}
      <section className="relative py-20 md:py-28 bg-[#080B12] overflow-hidden grain">
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-14">
            <Eyebrow>{POA.doors.eyebrow}</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream mb-4">{POA.doors.h}</h2>
            <p className="text-lg text-cream/65">{POA.doors.sub}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {POA.doors.items.map((d, i) => (
              <motion.div key={d.n} data-testid={`poa-door-${i}`}
                initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-3xl p-8 border border-[#C9A24B]/25 hover:border-[#C9A24B]/55 transition-colors bg-[#0B1020]/40 flex flex-col">
                <span className="font-display text-4xl text-[#E7CC86]/50">{d.n}</span>
                <p className="text-xs tracking-[0.18em] uppercase text-[#E7CC86]/70 mt-4 mb-3">{d.tag}</p>
                <h3 className="font-display text-2xl text-cream leading-tight">{d.title}</h3>
                <p className="text-cream/60 text-sm mt-4 leading-relaxed">{d.body}</p>
                <p className="mt-5 pt-4 border-t border-[#C9A24B]/15 font-serif italic text-[#E7CC86]">{d.pathway}</p>
                {d.testimonial && (
                  <div data-testid={`poa-door-quote-${i}`} className="mt-5 pt-5 border-t border-[#C9A24B]/15">
                    <p className="font-serif italic text-cream/70 text-sm leading-relaxed">“{d.testimonial.quote}”</p>
                    <p className="text-[#E7CC86] text-sm font-semibold mt-3">{d.testimonial.name}</p>
                    <p className="text-cream/45 text-xs">{d.testimonial.title}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE FIRST 7 DAYS (§36) ─────────────────────────────────────── */}
      <section className="relative py-20 md:py-28 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-10">
            <Eyebrow>{POA.first7.eyebrow}</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream mb-4">{POA.first7.h}</h2>
            <p className="text-lg text-cream/65">{POA.first7.sub}</p>
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            {POA.first7.tabs.map((t) => (
              <button key={t.key} data-testid={`poa-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`px-5 py-2.5 rounded-full text-sm transition-all ${tab === t.key ? "btn-gold font-semibold" : "border border-[#C9A24B]/30 text-cream/70 hover:border-[#C9A24B]/60"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {activeTab.days.map((d, i) => (
              <motion.div key={d} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="glass rounded-2xl p-5">
                <span className="text-xs tracking-[0.2em] uppercase text-[#E7CC86]/60">Day {i + 1}</span>
                <p className="font-display text-lg text-cream mt-1.5 leading-tight">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOUNDING CIRCLE OFFER (§37–§39) ────────────────────────────
          Benefit order is deliberate: Portals of Awe membership leads, the wearable
          follows. The purchase panel stays clean — no testimonials, no extra messaging. */}
      <section id="poa-offer" className="relative py-20 md:py-28 bg-[#080B12] overflow-hidden grain">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[55rem] h-[55rem] radial-glow blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Eyebrow>{POA.offer.eyebrow}</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream mb-5 leading-[1.1]">{POA.offer.h}</h2>
            <p className="text-lg text-cream/65">{POA.offer.body}</p>
          </div>

          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6 items-start">
            <div className="glass rounded-3xl p-8">
              <p className="text-xs tracking-[0.2em] uppercase text-[#E7CC86]/70 mb-5">Your Founding Membership Includes</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {POA.offer.includes.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-cream/75 text-[15px] leading-snug">
                    <Check size={17} className="text-[#E7CC86] shrink-0 mt-0.5" /> {it}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl p-8 border border-[#C9A24B]/35 bg-[#0B1020]/60 text-center ring-gold-frame">
              <p className="text-xs tracking-[0.2em] uppercase text-cream/50 mb-2">Founding Member Investment</p>
              <p className="font-display font-bold text-5xl text-gold mb-1">{POA.price}</p>
              <p className="text-cream/50 text-sm mb-5">{POA.offer.priceNote}</p>

              <div data-testid="poa-scarcity" className="mb-6 text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs tracking-[0.14em] uppercase text-[#E7CC86]/80">{POA.offer.scarcity.claimed} of {POA.offer.scarcity.total} places claimed</span>
                  <span className="text-xs text-cream/45">{POA.offer.scarcity.total - POA.offer.scarcity.claimed} left</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: `${(POA.offer.scarcity.claimed / POA.offer.scarcity.total) * 100}%` }} viewport={{ once: true }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full bg-gradient-to-r from-[#8B6520] via-[#C9A24B] to-[#E7CC86]" />
                </div>
              </div>

              <button data-testid="poa-offer-join" onClick={openJoin} className="btn-gold w-full py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2">
                Join the Founding Circle <ArrowRight size={20} />
              </button>
              <p className="text-xs text-cream/35 mt-4 leading-relaxed">{POA.offer.disclaimer}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT COMES ALIVE (§40) + human proof ───────────────────────── */}
      <section className="relative py-20 md:py-28 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-12">
            <Eyebrow>{POA.comesAlive.eyebrow}</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream leading-[1.1]">{POA.comesAlive.h}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {POA.comesAlive.items.map((it, i) => (
              <motion.div key={it.t} data-testid={`poa-alive-${i}`}
                initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="glass rounded-2xl p-6">
                <h3 className="font-display text-xl text-cream leading-tight">{it.t}</h3>
                <p className="text-cream/55 text-sm mt-2.5 leading-relaxed">{it.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 pt-14 border-t border-[#C9A24B]/15">
            <Quote testid="poa-quote-human" {...POA.comesAlive.testimonial} />
          </div>
        </div>
      </section>

      {/* ── SUPPORTED BY VITAL NEURO + its own proof ───────────────────
          Placed after the Portals of Awe benefits so the technology reads as an
          enabling part of the membership rather than the offer itself. */}
      <section className="relative py-20 md:py-28 bg-[#080B12] overflow-hidden grain">
        <div className="aurora-weave slow" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div data-testid="poa-headset-visual" initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="relative rounded-3xl overflow-hidden ring-gold-frame bg-gradient-to-b from-[#12203a] to-[#05070C]">
              <div className="absolute -inset-6 radial-glow blur-2xl pointer-events-none" />
              <img loading="lazy" src={POA.vitalNeuro.headsetImg} alt="Vital Neuro EEG wearable" className="relative w-full h-auto object-contain max-h-[460px] mx-auto"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </motion.div>
            <div>
              <Eyebrow>{POA.vitalNeuro.eyebrow}</Eyebrow>
              <p className="text-lg text-cream/72 leading-relaxed">{POA.vitalNeuro.body}</p>
              <p className="text-base text-cream/60 leading-relaxed mt-5">{POA.vitalNeuro.headsetLine}</p>
              <div className="flex flex-wrap gap-2.5 mt-8">
                {POA.vitalNeuro.outcomes.map((o) => (
                  <span key={o} className="px-4 py-2 rounded-full text-sm border border-[#C9A24B]/25 text-cream/70">{o}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-16 pt-14 border-t border-[#C9A24B]/15">
            <Quote testid="poa-quote-vitalneuro" {...POA.vitalNeuro.testimonial} />
          </div>
        </div>
      </section>

      {/* ── FAQ (§42) ──────────────────────────────────────────────────── */}
      <section className="relative py-20 md:py-28 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8">
          <div className="mb-10">
            <Eyebrow>Questions</Eyebrow>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream">Frequently asked.</h2>
          </div>
          <div className="space-y-3">
            {POA.faq.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} data-testid={`poa-faq-${i}`} className="glass rounded-2xl overflow-hidden">
                  <button onClick={() => setOpenFaq(open ? -1 : i)} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5">
                    <span className="font-display text-lg text-cream">{f.q}</span>
                    {open ? <Minus size={20} className="text-[#E7CC86] shrink-0" /> : <Plus size={20} className="text-[#E7CC86] shrink-0" />}
                  </button>
                  {open && (
                    <div className="px-6 pb-6 -mt-1 space-y-3">
                      {f.a.split("\n\n").map((para, k) => (
                        <p key={k} className="text-cream/65 leading-relaxed">{para}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── THE DOORWAY IS OPEN (§43) ──────────────────────────────────
          Nothing further may be added here: no extra testimonials, features or copy.
          The full investment stays the visual anchor; the payment plan is secondary. */}
      <section className="relative py-24 md:py-32 bg-[#080B12] overflow-hidden grain">
        <div className="aurora-weave" />
        <div className="absolute inset-0 hex-texture opacity-40 pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 text-center">
          <Eyebrow>{POA.finalCta.eyebrow}</Eyebrow>
          <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl text-cream mb-6 leading-[1.08]">{POA.finalCta.h}</h2>
          <p className="text-lg text-cream/65 mb-6 max-w-xl mx-auto">{POA.finalCta.body}</p>
          <p className="text-sm tracking-[0.04em] text-[#E7CC86]/75 mb-9">{POA.finalCta.limit}</p>

          <button data-testid="poa-final-join" onClick={openJoin} className="btn-gold px-9 py-4 rounded-full text-base font-semibold inline-flex items-center gap-2">
            {POA.finalCta.cta} <ArrowRight size={18} />
          </button>

          <p className="text-cream/60 text-sm mt-6">{POA.finalCta.planLine}</p>
          <p className="text-cream/40 text-xs mt-1.5">{POA.finalCta.planNote}</p>

          <p className="text-cream/30 text-xs mt-14 max-w-2xl mx-auto leading-relaxed">{POA.footerDisclaimer}</p>
        </div>
      </section>

      <LeadModal
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        title="Portals of Awe · Founding Circle"
        subtitle="Request your place in the Founding Circle"
        source={POA.source}
        avatar="community"
        interest={`founding-circle ${POA.price}`}
        withMessage
        cta="Request My Place"
      />

      {walkthroughOpen && (
        <div data-testid="poa-walkthrough-modal" className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setWalkthroughOpen(false)}>
          <button data-testid="poa-walkthrough-close" className="absolute top-6 right-6 text-cream/80 hover:text-[#E7CC86] transition-colors" onClick={() => setWalkthroughOpen(false)}>
            <X size={34} />
          </button>
          <video src="/assets/video/poa-app-90s.mp4" poster="/assets/video/poa-app-poster.jpg" controls autoPlay playsInline
            className="max-h-[86vh] w-auto rounded-2xl ring-gold-frame bg-black" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </MinimalShell>
  );
}
